/**
 * What the field shows besides the rules: "+10 / +25 / +100" pops where bricks broke and the
 * chips that fall from them. Pure; units are studs, like the engine.
 */
import type { BreakerEvent } from "./engine";
import type { Brick } from "./levels";
import { ROWS } from "./world";

export const POP_LIFE = 0.8; // s
const POP_RISE = 1.2; // studs/s
const GRAVITY = 30; // studs/s²

export interface Pop {
  x: number;
  y: number;
  text: string;
  gold: boolean;
  age: number;
}

export interface Chip {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  spin: number;
  color: string;
}

export interface Scene {
  pops: Pop[];
  chips: Chip[];
}

export const emptyScene = (): Scene => ({ pops: [], chips: [] });

export function spawnPop(brick: Brick, points: number): Pop {
  return { x: brick.col + brick.len / 2, y: brick.row + 0.5, text: `+${points}`, gold: brick.kind === "gold", age: 0 };
}

/** Two small tiles flying out of a broken brick, one each way. */
export function spawnChips(brick: Brick): Chip[] {
  const x = brick.col + brick.len / 2, y = brick.row + 0.5;
  return [-1, 1].map((side) => ({ x: x + side * 0.3, y, vx: side * 2.2, vy: -3, rot: 0, spin: side * 7, color: brick.color }));
}

/** Adds pops (and, unless motion is reduced, chips) for the bricks that broke this frame. */
export function addEvents(scene: Scene, events: BreakerEvent[], reduced = false): Scene {
  const breaks = events.filter((e): e is Extract<BreakerEvent, { kind: "break" }> => e.kind === "break");
  if (!breaks.length) return scene;
  return {
    pops: [...scene.pops, ...breaks.map((e) => spawnPop(e.brick, e.points))],
    chips: reduced ? scene.chips : [...scene.chips, ...breaks.flatMap((e) => spawnChips(e.brick))],
  };
}

/** Pops rise and fade; chips fall and spin; finished ones are dropped. Reduced motion: pops only fade. */
export function stepScene(scene: Scene, dt: number, reduced = false): Scene {
  if (!scene.pops.length && !scene.chips.length) return scene;
  return {
    pops: scene.pops
      .map((p) => ({ ...p, age: p.age + dt, y: reduced ? p.y : p.y - POP_RISE * dt }))
      .filter((p) => p.age < POP_LIFE),
    chips: scene.chips
      .map((c) => ({ ...c, x: c.x + c.vx * dt, y: c.y + c.vy * dt, vy: c.vy + GRAVITY * dt, rot: c.rot + c.spin * dt }))
      .filter((c) => c.y < ROWS + 1),
  };
}
